import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, CalendarClock, Paperclip, X, FileText, Search, Loader2, Eye,
  Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Eraser, ChevronDown, Folder,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { logger } from '../../utils/logger';
import { DraftEmailReviewDialog } from '../../components/admin/DraftEmailReviewDialog';
import { SavedEmailAttachment } from '../../components/admin/SavedEmailAttachment';
import { EmailAttachmentBudget } from '../../components/admin/EmailAttachmentBudget';
import { useEmailLimits } from '../../hooks/useEmailLimits';
import { formatEmailBytes } from '../../lib/emailDrafts';
import { DRAFT_API, saveEmailDraft, reviewEmailDraft, submitEmailDraft, editableDraft, draftError, nextSchedule, caymanDate } from '../../lib/emailDrafts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  quote:          { label: 'Quote',          hue: 'text-blue-700 bg-blue-50 border-blue-200' },
  invoice:        { label: 'Invoice',        hue: 'text-purple-700 bg-purple-50 border-purple-200' },
  receipt:        { label: 'Receipt',        hue: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  microchip_cert: { label: 'Microchip',      hue: 'text-amber-700 bg-amber-50 border-amber-200' },
  rabies_cert:    { label: 'Rabies Cert',    hue: 'text-rose-700 bg-rose-50 border-rose-200' },
  health_cert:    { label: 'Health Cert',    hue: 'text-teal-700 bg-teal-50 border-teal-200' },
  consultation_summary: { label: 'Consult. Summary', hue: 'text-primary bg-primary/10 border-primary/30' },
};

const humanBytes = formatEmailBytes;

// ---------------------------------------------------------------------------
// Rich text editor — contentEditable + document.execCommand.
// execCommand is technically deprecated but ships in every browser and is
// the fastest way to get bold/italic/lists/link without pulling in TipTap.
// ---------------------------------------------------------------------------
const RichTextEditor = ({ valueRef, placeholder, html, disabled }) => {
  const editorRef = useRef(null);
  useEffect(() => { if (html !== null && html !== undefined && editorRef.current) editorRef.current.innerHTML = html; }, [html]);
  useEffect(() => {
    valueRef.current = () => editorRef.current?.innerHTML || '';
  }, [valueRef]);

  const exec = useCallback((cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
  }, []);

  const promptLink = () => {
    const sel = window.getSelection();
    const hasSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;
    const url = window.prompt(hasSelection ? 'Link URL:' : 'Paste a URL — it will be inserted as a clickable link:', 'https://');
    if (!url) return;
    editorRef.current?.focus();
    if (hasSelection) {
      document.execCommand('createLink', false, url);
    } else {
      document.execCommand('insertHTML', false, `<a href="${url}" target="_blank" rel="noopener">${url}</a>`);
    }
  };

  const btn = "p-1.5 rounded hover:bg-gray-100 text-foreground/70 hover:text-foreground";

  return (
    <div className="border border-border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/40 bg-white">
      <div className="flex items-center gap-0.5 border-b border-border px-2 py-1.5 bg-gray-50/70 flex-wrap">
        <button type="button" onClick={() => exec('bold')} data-testid="rte-bold" className={btn} title="Bold (Ctrl+B)"><Bold className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('italic')} data-testid="rte-italic" className={btn} title="Italic (Ctrl+I)"><Italic className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('underline')} data-testid="rte-underline" className={btn} title="Underline (Ctrl+U)"><Underline className="w-4 h-4" /></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" onClick={() => exec('insertUnorderedList')} data-testid="rte-ul" className={btn} title="Bulleted list"><List className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('insertOrderedList')} data-testid="rte-ol" className={btn} title="Numbered list"><ListOrdered className="w-4 h-4" /></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" onClick={promptLink} data-testid="rte-link" className={btn} title="Insert link"><LinkIcon className="w-4 h-4" /></button>
        <button type="button" onClick={() => exec('removeFormat')} data-testid="rte-clear" className={btn} title="Clear formatting"><Eraser className="w-4 h-4" /></button>
      </div>
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-testid="composer-body"
        data-placeholder={placeholder || 'Write your message…'}
        className="min-h-[240px] max-h-[520px] overflow-y-auto p-4 text-sm outline-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-primary [&_a]:underline empty:before:content-[attr(data-placeholder)] empty:before:text-foreground/40"
      />
    </div>
  );
};

const TRAINING_TEMPLATE = {
  subject: 'Pet Travel Essentials — 60-minute front desk training',
  body: (
    '<p>Hi Dr. [Name],</p>' +
    '<p>I wanted to introduce your team to a short in-clinic session we run for veterinary practices: ' +
    '<strong>Pet Travel Essentials — Front Desk Training for Veterinary Teams</strong>.</p>' +
    '<p>International pet travel is complex and time-sensitive, and the very first person a client speaks ' +
    'to at your clinic often sets the whole case up (or down) for success. Over 60 focused minutes, your ' +
    'reception and client-care team will learn to:</p>' +
    '<ul>' +
    '<li>Recognise a pet-travel case at the first enquiry</li>' +
    '<li>Ask the right questions from the start</li>' +
    '<li>Avoid the common pitfalls that delay or prevent travel</li>' +
    '<li>Sequence microchip &amp; rabies documentation correctly</li>' +
    '<li>Know when to escalate to a vet or refer to Pawsome Ventures</li>' +
    '</ul>' +
    "<p>You'll find the full outline, the take-home reference materials your team receives, and pricing in " +
    'the attached flyer. Happy to answer any questions or find a time that suits your clinic.</p>'
  ),
  attachFlyer: true,
  attachUrl: '/api/brand/vet-training-flyer.pdf',
  attachFilename: 'Pawsome-Vet-Training-Flyer.pdf',
};

const UK_GUIDE_TEMPLATE = {
  subject: 'Your Cayman → UK pet relocation guide',
  body: (
    '<p>Hi [Name],</p>' +
    "<p>As promised, here's the short guide covering everything you need to know for your pet's relocation from Cayman to the UK — the lead-time you need, timeline of vet visits and paperwork, booking logistics on the BA direct flight, UK-side broker options, and what to expect on the day of travel (including the supply list to pack).</p>" +
    "<p>You'll find it attached as a PDF, and there is also a shareable web version you can bookmark: <a href=\"https://pawsomeventures.ky/guides/cayman-to-uk\">pawsomeventures.ky/guides/cayman-to-uk</a></p>" +
    "<p>Let me know if any part of it raises a question — happy to jump on a call to walk through your pet's specific case.</p>"
  ),
  attachUrl: '/api/brand/cayman-to-uk-guide.pdf',
  attachFilename: 'Pawsome-Cayman-to-UK-Pet-Relocation-Guide.pdf',
};

// Full response to a new client enquiry — INBOUND (client moving to Cayman).
// Pairs with the Benefits & Scope flyer that gets auto-attached. Leaves USD
// pricing as placeholders so the admin fills them in per-lead.
const ENQUIRY_REPLY_IMPORTS_TEMPLATE = {
  subject: 'Bringing your pet(s) to the Cayman Islands — how we can help',
  body: (
    '<p>Hi [Name],</p>' +
    "<p>Thank you for reaching out about bringing your pet(s) to the Cayman Islands. Below is a straight answer to each of your questions — the attached one-page flyer summarises the benefits and scope at a glance.</p>" +

    '<p><strong>1. Pricing — what to expect</strong></p>' +
    '<p>Our fees are quoted per case (not per pet), because most of the work — permit, paperwork prep, vet coordination, airline liaison, arrival — scales less than 2× when pets travel together. Typical ranges (USD):</p>' +
    '<ul>' +
    '<li>Full-service import for one pet from most origins: <strong>US$ [range]</strong></li>' +
    '<li>Two pets travelling together: <strong>US$ [range]</strong> (multi-pet discount is built in)</li>' +
    '</ul>' +
    '<p>Third-party costs (Cayman import permit, origin-country vet visits, endorsement, rabies FAVN if required, IATA crate, airline cargo, Cayman customs broker) are billed at cost with receipts — see the flyer for the full breakdown.</p>' +

    '<p><strong>2. Benefits and timing — the honest version</strong></p>' +
    "<p>Beyond the paperwork and coordination, you get: Cayman import permit submitted correctly the first time (the #1 cause of delay), vet visits sequenced so a single mis-timed vaccination doesn't push travel back by weeks, one point of contact instead of five, and the small details that break trips (ISO-readable microchip, exact certificate wording, airline crate acclimatisation rules).</p>" +
    '<p><em>On official timelines:</em> we cannot expedite Cayman Department of Agriculture processing. What we save you is preparation and rework time — often several weeks — not the government clock. Mandatory waiting periods between rabies vaccination and travel remain unchanged.</p>' +

    '<p><strong>3. Scope of assistance</strong></p>' +
    "<p>Included in our fee: record review, rabies/FAVN coordination, vet appointment scheduling, health certificate + origin-country endorsement, Cayman import permit, airline &amp; cargo liaison, Cayman customs &amp; arrival clearance. Optional add-ons: airport pickup + home delivery, in-Cayman vet registration &amp; first check-up. IATA-compliant crates are sourced at cost. The attached flyer has the full list.</p>" +

    '<p><strong>4. Planning &amp; lead time</strong></p>' +
    "<p>We recommend <strong>8–12 weeks</strong> from first contact to travel day. Common delay causes: microchip not ISO-readable, rabies certificate wording/date off by a day, wrong endorsement office used, permit submitted with the wrong airline confirmation.</p>" +
    '<p>To send an itemised quote and timeline, please share:</p>' +
    '<ol>' +
    '<li>Where you are moving from (city + airport)</li>' +
    '<li>Approximate travel window</li>' +
    '<li>Breed and current weight of each pet</li>' +
    '<li>Date of most recent rabies vaccination + microchip number (photos of the certificates are perfect)</li>' +
    '<li>Whether you would like the optional airport pickup + home delivery in Cayman</li>' +
    '</ol>' +
    "<p>Once I have those, I'll send an itemised quote and written timeline within one business day. If flights or approvals are ever delayed, we don't charge a second fee — we rebook (airline change fees pass through at cost) and keep the paperwork current so nothing lapses in the meantime.</p>" +

    "<p>Happy to jump on a 20-minute call if that's easier than exchanging emails — just suggest a couple of times that work for you.</p>"
  ),
  attachUrl: '/api/brand/benefits-scope-flyer.pdf',
  attachFilename: 'Pawsome-Benefits-and-Scope.pdf',
};

// Full response to a new client enquiry — OUTBOUND (client leaving Cayman).
const ENQUIRY_REPLY_EXPORTS_TEMPLATE = {
  subject: 'Relocating your pet(s) from the Cayman Islands — how we can help',
  body: (
    '<p>Hi [Name],</p>' +
    "<p>Thank you for reaching out about relocating your pet(s) from the Cayman Islands. Below is a straight answer to each of your questions — the attached one-page flyer summarises the benefits and scope at a glance.</p>" +

    '<p><strong>1. Pricing — what to expect</strong></p>' +
    '<p>Our fees are quoted per case (not per pet), because most of the work — destination research, paperwork prep, vet coordination, airline liaison, arrival — scales less than 2× when pets travel together. Typical ranges (USD):</p>' +
    '<ul>' +
    '<li>Full-service export for one pet to most destinations: <strong>US$ [range]</strong></li>' +
    '<li>Two pets travelling together: <strong>US$ [range]</strong> (multi-pet discount is built in)</li>' +
    '</ul>' +
    "<p>Third-party costs (destination import permit if required, Cayman vet visits, government endorsement, rabies FAVN if required for the destination, IATA crate, airline cargo, destination-side broker) are billed at cost with receipts — see the flyer for the full breakdown.</p>" +

    '<p><strong>2. Benefits and timing — the honest version</strong></p>' +
    "<p>Beyond the paperwork and coordination, you get: destination requirements researched country-specifically (each is different), vet visits sequenced so a single mis-timed vaccination doesn't push travel back by weeks, one point of contact instead of five, and the small details that break trips (ISO-readable microchip, exact certificate wording, airline crate acclimatisation rules, destination broker booked in advance).</p>" +
    "<p><em>On official timelines:</em> we cannot expedite destination government processing. What we save you is preparation and rework time — often several weeks — not the destination's clock. Mandatory waiting periods (e.g. between rabies vaccination and travel) remain unchanged.</p>" +

    '<p><strong>3. Scope of assistance</strong></p>' +
    '<p>Included in our fee: destination requirement research, rabies/FAVN scheduling, vet appointment sequencing, health certificate + Cayman government endorsement, destination import permit (where applicable), airline &amp; cargo liaison, destination-side broker &amp; arrival clearance. Optional add-ons: destination airport pickup + home delivery. IATA-compliant crates are sourced at cost. The attached flyer has the full list.</p>' +

    '<p><strong>4. Planning &amp; lead time</strong></p>' +
    "<p>We recommend <strong>8–12 weeks</strong> from first contact to travel day (longer for destinations that require rabies antibody testing — e.g. UK, EU, Japan, Australia — as the FAVN test has mandatory waiting periods). Common delay causes: microchip not ISO-readable, rabies certificate wording/date off by a day, wrong destination-country requirements referenced, permit submitted with the wrong airline confirmation.</p>" +
    '<p>To send an itemised quote and timeline, please share:</p>' +
    '<ol>' +
    '<li>Destination country + city + airport</li>' +
    '<li>Approximate travel window</li>' +
    '<li>Breed and current weight of each pet</li>' +
    '<li>Date of most recent rabies vaccination + microchip number (photos of the certificates are perfect)</li>' +
    '<li>Whether you would like the optional airport pickup + home delivery at destination</li>' +
    '</ol>' +
    "<p>Once I have those, I'll send an itemised quote and written timeline within one business day. If flights or approvals are ever delayed, we don't charge a second fee — we rebook (airline change fees pass through at cost) and keep the paperwork current so nothing lapses in the meantime.</p>" +

    "<p>Happy to jump on a 20-minute call if that's easier than exchanging emails — just suggest a couple of times that work for you.</p>"
  ),
  attachUrl: '/api/brand/benefits-scope-flyer.pdf',
  attachFilename: 'Pawsome-Benefits-and-Scope.pdf',
};

const EmailComposer = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const password = sessionStorage.getItem('admin_password');
  const auth = { Authorization: `Basic ${btoa(':' + password)}` };
  const { limits, error: limitsError, loading: limitsLoading, retry: retryLimits } = useEmailLimits(password);
  const [attachmentError, setAttachmentError] = useState('');

  const [to, setTo] = useState(params.get('to') || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(params.get('subject') || '');
  const bodyHtmlRef = useRef(() => '');   // populated by RTE
  const [library, setLibrary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [snippets, setSnippets] = useState([]);
  const [snippetOpen, setSnippetOpen] = useState(false);
  const [selectedRefs, setSelectedRefs] = useState([]);
  const [files, setFiles] = useState([]);
  const [pickerType, setPickerType] = useState('quote');
  const [pickerSearch, setPickerSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [draft, setDraft] = useState(null);
  const currentDraft = useRef(null);
  const [draftLoading, setDraftLoading] = useState(Boolean(params.get('draft')));
  const [draftErrorText, setDraftErrorText] = useState('');
  const [savedUploads, setSavedUploads] = useState([]);
  const [editorHtml, setEditorHtml] = useState(null);
  const [plainBody, setPlainBody] = useState('');
  const [scheduleMode, setScheduleMode] = useState(false);
  const [localTime, setLocalTime] = useState(nextSchedule);
  const [dirty, setDirty] = useState(false);
  const readOnly = !editableDraft(draft);
  const leave = (path) => { if (!dirty || window.confirm('Leave without saving your latest email changes?')) navigate(path); };
  useEffect(() => {
    const warn = (event) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const sendLock = useRef(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [clinics, setClinics] = useState([]);   // vet clinics — only used when template=training
  const [libraryFolders, setLibraryFolders] = useState([]);
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [libraryActiveFolder, setLibraryActiveFolder] = useState(null);
  const [librarySearch, setLibrarySearch] = useState('');
  const [selectedLibFiles, setSelectedLibFiles] = useState([]);  // full file docs
  const template = params.get('template');

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    (async () => {
      try {
        const [libRes, ctcRes, snpRes] = await Promise.all([
          axios.get(`${API}/admin/emails/attachable`, { headers: auth }),
          axios.get(`${API}/admin/emails/contacts`, { headers: auth }),
          axios.get(`${API}/admin/email-snippets`, { headers: auth }),
        ]);
        setLibrary(libRes.data);
        setContacts(ctcRes.data.contacts || []);
        setSnippets(snpRes.data.snippets || []);
        const attach = params.get('attach');
        if (!params.get('draft') && attach && attach.includes(':')) {
          const [t, id] = attach.split(':');
          const it = (libRes.data[t]?.items || []).find((x) => x.id === id);
          if (it) setSelectedRefs([{ type: t, ...it }]);
        }

          // Preset templates deep-linked from the dashboard etc.
          if (!params.get('draft') && ['training', 'uk-guide', 'enquiry-reply-imports', 'enquiry-reply-exports'].includes(template)) {
            const tpl = template === 'training'
              ? TRAINING_TEMPLATE
              : template === 'uk-guide'
                ? UK_GUIDE_TEMPLATE
                : template === 'enquiry-reply-imports'
                  ? ENQUIRY_REPLY_IMPORTS_TEMPLATE
                  : ENQUIRY_REPLY_EXPORTS_TEMPLATE;
            if (!subject) setSubject(tpl.subject);
            requestAnimationFrame(() => {
              const editor = document.querySelector('[data-testid="composer-body"]');
              if (editor && !editor.innerText.trim()) {
                editor.innerHTML = tpl.body;
              }
            });
            try {
              const flyerRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}${tpl.attachUrl}`);
              if (flyerRes.ok) {
                const blob = await flyerRes.blob();
                const flyerFile = new File([blob], tpl.attachFilename, { type: 'application/pdf' });
                setFiles((prev) => (
                  prev.some((f) => f.name === flyerFile.name) ? prev : [...prev, flyerFile]
                ));
              }
            } catch (flyerErr) {
              logger.error('Failed to auto-attach template', flyerErr);
              toast.error('PDF could not be attached automatically — you can add it manually');
            }
          }
          // Vet clinic picker — only relevant for the training template.
          if (template === 'training') {
            try {
              const clinicsRes = await axios.get(`${API}/admin/vet-clinics`, { headers: auth });
              setClinics(clinicsRes.data.clinics || []);
            } catch (clinicErr) { /* non-fatal */ }
          }
      } catch (e) {
        logger.error('Composer load failed', e);
        toast.error('Failed to load composer data');
      }
      try {
        const { data } = await axios.get(`${API}/admin/emails?limit=8&category=compose`, { headers: auth });
        setRecentLogs(data.items || data || []);
      } catch (e) { /* non-fatal */ }
      // Library — folders + root files (for the picker in the right rail)
      try {
        const { data } = await axios.get(`${API}/admin/library/folders`, { headers: auth });
        setLibraryFolders(data.folders || []);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const draftId = params.get('draft');
  useEffect(() => {
    if (!draftId || currentDraft.current?.id === draftId || !password) return;
    let active = true;
    setDraftLoading(true); setDraftErrorText('');
    axios.get(`${DRAFT_API}/${draftId}`, { headers: auth }).then(async ({ data }) => {
      if (!active) return;
      currentDraft.current = data; setDraft(data); setTo(data.to_email); setCc(data.cc); setSubject(data.subject);
      setEditorHtml(data.body_format === 'html' ? data.body : ''); setPlainBody(data.body_format === 'plain' ? data.body : '');
      setSavedUploads(data.uploads); setFiles([]);
      setSelectedRefs(data.refs.map((r) => ({ ...r, number: r.id.slice(0, 8), client_name: '' })));
      setSelectedLibFiles(data.library_ids.map((id) => ({ id, filename: `Library file ${id.slice(0, 8)}` })));
      if (data.library_ids.length) {
        try {
          const response = await axios.get(`${API}/admin/library/files?include_all_folders=true`, { headers: auth });
          if (active) setSelectedLibFiles(data.library_ids.map((id) => response.data.files.find((f) => f.id === id) || { id, filename: `Unavailable file ${id.slice(0, 8)}` }));
        } catch { /* Reference is retained; review validates availability. */ }
      }
    }).catch((e) => { if (active) setDraftErrorText(draftError(e)); }).finally(() => { if (active) setDraftLoading(false); });
    return () => { active = false; };
    // The draft is loaded once per URL; unrelated picker requests never overwrite editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId]);

  useEffect(() => {
    if (!library) return;
    setSelectedRefs((refs) => refs.map((ref) => ({ ...ref, ...(library[ref.type]?.items.find((i) => i.id === ref.id) || {}) })));
  }, [library, draft?.id]);

  // Refetch library files when the picker's folder / search changes.
  useEffect(() => {
    if (!password) return;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (librarySearch) { params.set('search', librarySearch); params.set('include_all_folders', 'true'); }
        else if (libraryActiveFolder) params.set('folder_id', libraryActiveFolder);
        const { data } = await axios.get(`${API}/admin/library/files?${params.toString()}`, { headers: auth });
        setLibraryFiles(data.files || []);
      } catch (e) { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryActiveFolder, librarySearch]);

  const knownBytes = (uploads = files, libraryFiles = selectedLibFiles) => uploads.reduce((n, f) => n + f.size, 0) + savedUploads.reduce((n, f) => n + f.size_bytes, 0) + libraryFiles.reduce((n, f) => n + Number(f.size_bytes || 0), 0);
  const sizeError = 'Combined attachments exceed 25 MB. Remove or replace a file; files will not be converted to links.';
  const toggleLibFile = (file) => {
    const next = selectedLibFiles.some((x) => x.id === file.id) ? selectedLibFiles.filter((x) => x.id !== file.id) : [...selectedLibFiles, file];
    if (next.length > selectedLibFiles.length && limits && knownBytes(files, next) > limits.max_attachment_bytes) { setAttachmentError(sizeError); return; }
    setAttachmentError(''); setDirty(true); setSelectedLibFiles(next);
  };

  const filteredLibrary = useMemo(() => {
    if (!library) return [];
    const items = library[pickerType]?.items || [];
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.number || '').toLowerCase().includes(q) ||
      (it.client_name || '').toLowerCase().includes(q),
    );
  }, [library, pickerType, pickerSearch]);

  const toggleRef = (item) => {
    if (draft?.kind === 'consultation' && item.id === draft.source_id && pickerType === 'consultation_summary') return;
    setDirty(true);
    const key = `${pickerType}:${item.id}`;
    const exists = selectedRefs.some((r) => `${r.type}:${r.id}` === key);
    setSelectedRefs((prev) =>
      exists ? prev.filter((r) => `${r.type}:${r.id}` !== key) : [...prev, { type: pickerType, ...item }],
    );
  };
  const removeRef = (r) => { setDirty(true); setSelectedRefs((prev) => prev.filter((x) => !(x.type === r.type && x.id === r.id))); };

  const onFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    setAttachmentError('');
    if (!limits) { setAttachmentError('Wait for attachment limits to load before selecting files.'); return; }
    const combined = [...files, ...picked];
    if (combined.length + savedUploads.length > limits.max_upload_files) { setAttachmentError(`Up to ${limits.max_upload_files} files can be uploaded to one email.`); return; }
    if (knownBytes(combined) > limits.max_attachment_bytes) { setAttachmentError(sizeError); return; }
    setFiles(combined);
  };
  const removeFile = (idx) => { setAttachmentError(''); setDirty(true); setFiles((prev) => prev.filter((_, i) => i !== idx)); };

  const insertSnippet = (snippet) => {
    setDirty(true);
    setSnippetOpen(false);
    // Insert HTML into the RTE at cursor (or append). We reach into the DOM
    // rather than force a rerender so the user's other formatting stays put.
    const editor = document.querySelector('[data-testid="composer-body"]');
    if (!editor) return;
    editor.focus();
    // If empty, replace entirely
    if (!editor.innerText.trim()) {
      editor.innerHTML = snippet.body;
      return;
    }
    document.execCommand('insertHTML', false, snippet.body);
  };

  const remember = (data) => { currentDraft.current = data; setDraft(data); };
  const persist = async () => {
    if (!limits) throw new Error('Attachment limits unavailable');
    const data = await saveEmailDraft(currentDraft.current, {
      to_email: to, cc, subject, body: draft?.body_format === 'plain' ? plainBody : bodyHtmlRef.current(),
      body_format: draft?.body_format || 'html', kind: draft?.kind || 'compose', source_id: draft?.source_id || null,
      refs: selectedRefs.map(({ type, id }) => ({ type, id })), library_ids: selectedLibFiles.map((f) => f.id), keep_upload_ids: savedUploads.map((f) => f.id),
    }, files, auth);
    remember(data); setSavedUploads(data.uploads); setFiles([]); setDirty(false);
    navigate(`/admin/email-composer?draft=${data.id}`, { replace: true });
    return data;
  };
  const saveDraft = async () => {
    if (sendLock.current || readOnly || !limits) return;
    sendLock.current = true; setSending(true); setDraftErrorText('');
    try { await persist(); toast.success('Email draft saved'); }
    catch (e) { setDraftErrorText(draftError(e)); }
    finally { sendLock.current = false; setSending(false); }
  };
  const handleSend = async (schedule = false) => {
    if (sendLock.current || readOnly || !limits) return;
    sendLock.current = true; setScheduleMode(schedule); setPreviewLoading(true); setPreviewError(''); setPreviewOpen(true);
    try {
      const saved = await persist();
      remember(await reviewEmailDraft(saved, auth));
    } catch (e) { setPreviewError(draftError(e)); }
    finally { sendLock.current = false; setPreviewLoading(false); }
  };
  const confirmSend = async () => {
    if (sendLock.current || !draft?.preview_hash || previewError) return;
    sendLock.current = true; setSending(true);
    try {
      const data = await submitEmailDraft(draft, auth, scheduleMode ? localTime : null);
      remember(data);
      if (['scheduled', 'sent'].includes(data.status)) {
        toast.success(data.status === 'scheduled' ? `Scheduled for ${caymanDate(data.send_at)} · Cayman time` : `Email sent to ${data.to_email}`);
        navigate('/admin/email-drafts');
      } else { setPreviewOpen(false); setDraftErrorText(data.delivery_note || 'Check the delivery status before sending again.'); }
    } catch (e) {
      setPreviewError(draftError(e));
      // Reconcile a lost response without issuing another SMTP request.
      try { remember((await axios.get(`${DRAFT_API}/${draft.id}`, { headers: auth })).data); } catch { /* Keep the reviewed draft; server CAS prevents duplicate sending. */ }
    } finally { sendLock.current = false; setSending(false); }
  };
  const cancelSchedule = async () => {
    if (sendLock.current || !window.confirm('Cancel the scheduled send and return this email to editing?')) return;
    sendLock.current = true; setSending(true);
    try { remember((await axios.post(`${DRAFT_API}/${draft.id}/cancel`, { version: draft.version }, { headers: auth })).data); toast.success('Schedule cancelled'); }
    catch (e) { setDraftErrorText(draftError(e)); }
    finally { sendLock.current = false; setSending(false); }
  };

  const totalBytes = knownBytes();

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-white sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <button onClick={() => leave('/admin/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="composer-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">Compose Email</h1>
          <Button variant="outline" className="sm:ml-auto" data-testid="composer-drafts-link" onClick={() => leave('/admin/email-drafts')}>Drafts &amp; scheduled</Button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 pt-5 space-y-3">
        {draftLoading && <p role="status" data-testid="composer-draft-loading">Loading saved email…</p>}
        {draftErrorText && <p role="alert" className="text-sm text-red-700" data-testid="composer-draft-error">{draftErrorText}</p>}
        {limitsError && <div role="alert" data-testid="composer-limits-error" className="text-sm text-red-700">{limitsError}<Button variant="link" data-testid="composer-limits-retry" disabled={limitsLoading} onClick={retryLimits}>Retry</Button></div>}
        {readOnly && draft?.reviewed_html && <Button variant="outline" data-testid="composer-view-snapshot" onClick={() => { setPreviewError(''); setPreviewOpen(true); }}>View reviewed email &amp; attachments</Button>}
        {dirty && <p className="text-xs text-amber-800" data-testid="composer-unsaved">Unsaved changes</p>}
        {draft && <div className="flex flex-wrap items-center gap-3 border-l-2 border-teal-600 bg-teal-50 p-3" data-testid="composer-draft-status"><span className="text-sm">{draft.status === 'scheduled' ? `Scheduled for ${caymanDate(draft.send_at)} · Cayman time` : `Saved email · ${draft.status}`}</span>{draft.status === 'scheduled' && <Button variant="outline" size="sm" data-testid="composer-cancel-schedule" disabled={sending} onClick={cancelSchedule}>Cancel schedule &amp; edit</Button>}{draft.delivery_note && <p className="w-full text-sm text-red-700" data-testid="composer-delivery-note">{draft.delivery_note}</p>}</div>}
      </div>
      <fieldset onInput={(event) => { if (['composer-to', 'composer-cc', 'composer-subject', 'composer-body', 'composer-file-input'].includes(event.target.closest('[data-testid]')?.dataset.testid)) setDirty(true); }} disabled={sending || previewLoading || draftLoading || readOnly || Boolean(draftId && !draft)} className="container mx-auto min-w-0 px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="min-w-0 lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-5 space-y-4">
              {template === 'training' && clinics.length > 0 && (
                <div className="rounded-lg bg-teal-50 border border-teal-200 p-3" data-testid="composer-clinic-picker">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-xs font-semibold text-teal-800 uppercase tracking-wide">
                      Pick a clinic from your address book
                    </div>
                    <button
                      type="button"
                      onClick={() => leave('/admin/vet-clinics')}
                      data-testid="composer-manage-clinics"
                      className="text-[11px] text-teal-700 hover:underline"
                    >
                      Manage clinics →
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {clinics.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => { setDirty(true); setTo(c.email); }}
                        data-testid={`composer-clinic-${c.id}`}
                        title={`${c.contact_name ? c.contact_name + ' · ' : ''}${c.email}${c.last_emailed_at ? ' · last emailed ' + new Date(c.last_emailed_at).toLocaleDateString() : ''}`}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                          to === c.email
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-foreground/80 border-teal-200 hover:border-primary hover:bg-primary/5'
                        }`}
                      >
                        {c.clinic_name}
                        {c.last_emailed_at && to !== c.email && (
                          <span className="ml-1 text-[10px] text-foreground/40">• already emailed</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label>To *</Label>
                <Input
                  data-testid="composer-to"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="client@example.com"
                  type="email"
                  list="composer-contacts"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label>Cc (comma-separated)</Label>
                <Input
                  data-testid="composer-cc"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="teammate@pawsomeventures.ky"
                  list="composer-contacts"
                  autoComplete="off"
                />
                <p className="mt-1 text-xs text-foreground/50">Autocomplete pulls from your address book + previously-emailed clients ({contacts.length} on file).</p>
              </div>
              <datalist id="composer-contacts">
                {contacts.map((c) => (
                  <option key={c.email} value={c.email}>{c.name || c.email}</option>
                ))}
              </datalist>
              <div>
                <Label>Subject *</Label>
                <Input
                  data-testid="composer-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Your Pawsome Ventures documents"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label>Message *</Label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setSnippetOpen((v) => !v)}
                      data-testid="composer-insert-snippet"
                      className="text-xs px-2.5 py-1 rounded-full border border-primary/40 text-primary hover:bg-primary/5 flex items-center gap-1"
                    >
                      Insert snippet <ChevronDown className="w-3 h-3" />
                    </button>
                    {snippetOpen && (
                      <div data-testid="composer-snippet-menu" className="absolute right-0 top-full mt-1 z-20 w-64 bg-white border border-border rounded-lg shadow-lg py-1 max-h-72 overflow-y-auto">
                        {snippets.length === 0 && (
                          <div className="px-3 py-2 text-xs text-foreground/60">No snippets saved yet. Add some in Settings.</div>
                        )}
                        {snippets.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => insertSnippet(s)}
                            data-testid={`composer-snippet-${s.id}`}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-primary/5"
                          >
                            {s.title}
                          </button>
                        ))}
                        <div className="border-t border-border mt-1 pt-1">
                          <button
                            onClick={() => { setSnippetOpen(false); leave('/admin/settings'); }}
                            data-testid="composer-manage-snippets"
                            className="w-full text-left px-3 py-2 text-xs text-foreground/60 hover:bg-gray-50"
                          >
                            Manage snippets →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {draft?.body_format === 'plain' ? <textarea data-testid="composer-body" className="min-h-[240px] w-full rounded-md border p-3 text-sm" value={plainBody} onChange={(e) => setPlainBody(e.target.value)} /> : <RichTextEditor valueRef={bodyHtmlRef} html={editorHtml} disabled={sending || previewLoading || draftLoading || readOnly} placeholder={"Hi …\n\nPlease find your documents attached."} />}
                <p className="mt-1 text-xs text-foreground/50">A Pawsome Ventures signature block is added automatically at the bottom of every email.</p>
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Attachments ({selectedRefs.length + files.length + savedUploads.length + selectedLibFiles.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-2 space-y-3">
              {selectedRefs.length + files.length + savedUploads.length + selectedLibFiles.length === 0 && (
                <p className="text-sm text-foreground/50">Nothing attached yet. Pick documents from the library on the right, or upload one-off files below.</p>
              )}
              {selectedRefs.map((r) => (
                <div key={`${r.type}:${r.id}`} className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm break-all ${TYPE_META[r.type]?.hue || ''}`}>
                  <FileText className="w-4 h-4" />
                  <span className="font-semibold">{TYPE_META[r.type]?.label}</span>
                  <span>·</span>
                  <span className="font-mono">{r.number}</span>
                  <span className="text-foreground/60">— {r.client_name}</span>
                  <button disabled={draft?.kind === 'consultation' && r.id === draft.source_id} onClick={() => removeRef(r)} className="ml-auto p-1 hover:bg-white/50 rounded disabled:opacity-30" data-testid={`remove-ref-${r.type}-${r.id}`} aria-label="Remove document">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {draft && savedUploads.map((attachment) => <SavedEmailAttachment key={attachment.id} draftId={draft.id} attachment={attachment} auth={auth} onRemove={readOnly ? undefined : () => { setAttachmentError(''); setDirty(true); setSavedUploads((all) => all.filter((a) => a.id !== attachment.id)); }} />)}
              {selectedLibFiles.map((file) => <div key={file.id} className="flex min-w-0 items-center gap-2 border-b py-2 text-sm" data-testid={`attached-library-${file.id}`}><Folder size={16} className="shrink-0" /><span className="min-w-0 flex-1 break-all">{file.filename}</span><button data-testid={`remove-library-${file.id}`} aria-label="Remove library file" onClick={() => toggleLibFile(file)}><X size={16} /></button></div>)}
              {files.map((f, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-gray-50 px-3 py-2 text-sm break-all">
                  <Paperclip className="w-4 h-4" />
                  <span className="font-semibold">{f.name}</span>
                  <span className="text-foreground/60">· {humanBytes(f.size)}</span>
                  <button onClick={() => removeFile(i)} className="ml-auto p-1 hover:bg-white rounded" data-testid={`remove-file-${i}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}

              <div className="pt-2 border-t border-border flex flex-wrap gap-2 items-center justify-between">
                <label aria-disabled={!limits} className={`flex items-center gap-1.5 text-sm ${limits ? 'cursor-pointer text-primary hover:underline' : 'cursor-not-allowed text-gray-500'}`} data-testid="composer-upload-file">
                  <Paperclip className="w-4 h-4" /> {limits ? 'Attach file (photo, PDF…)' : limitsError ? 'Attachment selection unavailable' : 'Loading attachment limits…'}
                  <input type="file" data-testid="composer-file-input" multiple disabled={!limits} onChange={onFilesPicked} className="hidden" />
                </label>
                <EmailAttachmentBudget total={totalBytes} count={files.length + savedUploads.length} generatedCount={selectedRefs.length} limits={limits} />
                {attachmentError && <p role="alert" className="text-sm text-red-700" data-testid="composer-attachment-error">{attachmentError}</p>}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" onClick={saveDraft} disabled={!limits} data-testid="composer-save-draft"><Save className="mr-2 h-4 w-4" />{sending && !previewOpen ? 'Saving draft…' : 'Save draft'}</Button>
            <Button variant="outline" onClick={() => handleSend(true)} disabled={!limits} data-testid="composer-schedule"><CalendarClock className="mr-2 h-4 w-4" />Schedule send</Button>
            <Button onClick={() => handleSend(false)} disabled={sending || previewLoading || !limits} data-testid="composer-send-btn" className="min-w-[160px]">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                : previewLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading preview…</>
                : <><Eye className="w-4 h-4 mr-2" /> Preview &amp; send</>}
            </Button>
          </div>
        </div>

        {/* Right rail */}
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Attach from library</CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-2 space-y-3">
              <div className="flex flex-wrap gap-1">
                {Object.entries(TYPE_META).map(([key, meta]) => {
                  const count = library?.[key]?.items?.length || 0;
                  return (
                    <button
                      key={key}
                      onClick={() => setPickerType(key)}
                      data-testid={`picker-tab-${key}`}
                      className={`px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors ${pickerType === key ? meta.hue : 'text-foreground/60 border-border hover:bg-gray-50'}`}
                    >
                      {meta.label} <span className="opacity-60">({count})</span>
                    </button>
                  );
                })}
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
                <Input
                  data-testid="picker-search"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search by number or client"
                  className="pl-9"
                />
              </div>
              <div className="max-h-[420px] overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {filteredLibrary.length === 0 && (
                  <div className="p-4 text-sm text-foreground/50 text-center">No {TYPE_META[pickerType]?.label.toLowerCase()}s to attach.</div>
                )}
                {filteredLibrary.map((it) => {
                  const checked = selectedRefs.some((r) => r.type === pickerType && r.id === it.id);
                  return (
                    <label
                      key={it.id}
                      className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${checked ? 'bg-emerald-50/50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRef(it)}
                        data-testid={`picker-item-${pickerType}-${it.id}`}
                        className="mt-1"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-mono font-semibold text-foreground truncate">{it.number}</div>
                        <div className="text-xs text-foreground/60 truncate">{it.client_name}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Attach from File Library (arbitrary uploaded files, not doc-linked) */}
          <Card data-testid="composer-library-picker">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span>From File Library</span>
                <button
                  type="button"
                  onClick={() => leave('/admin/library')}
                  data-testid="composer-manage-library"
                  className="text-[11px] font-normal text-primary hover:underline"
                >
                  Manage →
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-2 space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40" />
                <Input
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  placeholder="Search all files"
                  className="pl-9 h-9"
                  data-testid="library-picker-search"
                />
              </div>
              {libraryFolders.length > 0 && !librarySearch && (
                <div className="max-h-32 overflow-y-auto border border-border rounded-md p-1.5 bg-gray-50/50">
                  <button
                    type="button"
                    onClick={() => setLibraryActiveFolder(null)}
                    data-testid="library-picker-root"
                    className={`w-full text-left flex items-center gap-1 px-1.5 py-1 text-xs rounded ${libraryActiveFolder == null ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-white'}`}
                  >
                    <Folder className="w-3 h-3" /> Root
                  </button>
                  {libraryFolders.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setLibraryActiveFolder(f.id)}
                      data-testid={`library-picker-folder-${f.id}`}
                      className={`w-full text-left flex items-center gap-1 px-1.5 py-1 text-xs rounded ${libraryActiveFolder === f.id ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-white'}`}
                    >
                      <Folder className="w-3 h-3" /> {f.path}
                    </button>
                  ))}
                </div>
              )}
              <div className="max-h-72 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {libraryFiles.length === 0 && (
                  <div className="p-3 text-xs text-foreground/50 text-center">
                    {librarySearch ? 'No files match your search.' : 'This folder is empty.'}
                  </div>
                )}
                {libraryFiles.map((f) => {
                  const checked = selectedLibFiles.some((x) => x.id === f.id);
                  return (
                    <label
                      key={f.id}
                      data-testid={`library-picker-file-${f.id}`}
                      className={`flex items-start gap-2 px-2 py-2 cursor-pointer hover:bg-gray-50 ${checked ? 'bg-emerald-50/50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleLibFile(f)}
                        data-testid={`library-picker-checkbox-${f.id}`}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-foreground truncate">{f.filename}</div>
                        <div className="text-[10px] text-foreground/50">{humanBytes(f.size_bytes || 0)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              {selectedLibFiles.length > 0 && (
                <div className="text-[11px] text-emerald-700 font-medium" data-testid="library-picker-selected-count">
                  {selectedLibFiles.length} file{selectedLibFiles.length === 1 ? '' : 's'} selected from library
                </div>
              )}
            </CardContent>
          </Card>

          {recentLogs.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent sent</CardTitle>
              </CardHeader>
              <CardContent className="p-5 pt-2 space-y-2">
                {recentLogs.slice(0, 6).map((log) => {
                  const openCount = log.open_count || 0;
                  return (
                    <div key={log.id} className="text-xs border-b border-border last:border-0 pb-2 last:pb-0" data-testid={`recent-sent-${log.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold truncate">{log.subject}</div>
                        {openCount > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-semibold flex-shrink-0"
                            title={`Opened ${openCount}× · last ${log.last_opened_at ? new Date(log.last_opened_at).toLocaleString() : ''}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Opened{openCount > 1 ? ` ${openCount}×` : ''}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gray-50 border border-border text-foreground/50 text-[10px] font-semibold flex-shrink-0" title="Not opened yet">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                            Unopened
                          </span>
                        )}
                      </div>
                      <div className="text-foreground/60 truncate">→ {log.to_email}</div>
                      <div className="text-foreground/40">{(log.sent_at || '').slice(0, 16).replace('T', ' ')}</div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      </fieldset>

      {/* Preview modal — shows exactly what the recipient will see */}
      {previewOpen && <DraftEmailReviewDialog draft={draft} auth={auth} loading={previewLoading} busy={sending} error={previewError} scheduled={scheduleMode} inspect={readOnly} localTime={localTime} setLocalTime={setLocalTime} onClose={() => setPreviewOpen(false)} onConfirm={confirmSend} />}
    </div>
  );
};

export default EmailComposer;
